import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ApiError,
  api,
  type GuiaListItem,
  type TagConCount,
} from "@/lib/api";
import { useAuth, canEdit, isAdmin } from "@/lib/auth";
import GuiaEditorModal from "@/components/GuiaEditorModal";
import AyudaVista from "@/ui/AyudaVista";
import { ayudaGuias } from "@/components/ayuda/vistas/guias";
import { useToast } from "@/ui/Toast";

/** Base de conocimiento interna: lista de guias con filtros por tag. */
export default function Guias() {
  const me = useAuth((s) => s.user);
  const toast = useToast();
  const [items, setItems] = useState<GuiaListItem[]>([]);
  const [tagsDisponibles, setTagsDisponibles] = useState<TagConCount[]>([]);
  const [activos, setActivos] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [editorOpen, setEditorOpen] = useState(false);

  function isActivo(t: string): boolean {
    const k = t.toLowerCase();
    return activos.some((a) => a.toLowerCase() === k);
  }

  function toggleTag(t: string) {
    setActivos((prev) => {
      const k = t.toLowerCase();
      if (prev.some((a) => a.toLowerCase() === k)) {
        return prev.filter((a) => a.toLowerCase() !== k);
      }
      return [...prev, t];
    });
  }

  async function load() {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (q.trim()) params.set("q", q.trim());
      // Multi-tag: la API lo lee como list[str] via Query — un parametro
      // tag por cada tag activo. Multiples = AND (intersection).
      for (const t of activos) params.append("tag", t);
      const url = `/api/guias${params.toString() ? `?${params}` : ""}`;
      setItems(await api.get<GuiaListItem[]>(url));
    } catch (err) {
      const msg = err instanceof ApiError ? err.detail : "Error";
      toast.error("No pude cargar las guías", msg);
    } finally {
      setLoading(false);
    }
  }

  // Cargar tags disponibles una vez al montar (lista global; no depende
  // del filtro activo, asi siempre puedes elegir/quitar cualquiera).
  async function loadTags() {
    try {
      const rows = await api.get<TagConCount[]>("/api/guias/tags");
      setTagsDisponibles(rows);
    } catch {
      setTagsDisponibles([]);
    }
  }

  useEffect(() => {
    void loadTags();
  }, []);

  // Re-fetch al cambiar busqueda o filtros. Debounce para la busqueda.
  useEffect(() => {
    const t = setTimeout(() => void load(), 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, activos]);

  const sinResultados = !loading && items.length === 0;

  const [sincronizando, setSincronizando] = useState(false);

  async function sincronizarQueclink() {
    setSincronizando(true);
    try {
      const r = await api.post<{
        creadas: number;
        actualizadas: number;
        borradores_aparte: number;
        sin_cambios: number;
        imagenes_fallidas: number;
      }>("/api/queclink/kb/sincronizar", {});
      const partes = [
        r.creadas && `${r.creadas} nuevas`,
        r.actualizadas && `${r.actualizadas} actualizadas`,
        r.borradores_aparte && `${r.borradores_aparte} en borrador aparte`,
      ].filter(Boolean);
      toast.success(
        partes.length ? "Guías de Queclink al día" : "Ya estaba todo al día",
        partes.length
          ? partes.join(" · ")
          : `${r.sin_cambios} guías sin cambios en Queclink`,
      );
      if (r.imagenes_fallidas) {
        toast.error("Imágenes sin bajar", `${r.imagenes_fallidas} quedaron apuntando a Queclink`);
      }
      if (partes.length) void load();
    } catch (err) {
      toast.error("No se pudo sincronizar", err instanceof ApiError ? err.detail : "Error");
    } finally {
      setSincronizando(false);
    }
  }

  return (
    <div className="space-y-3">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex items-start gap-2">
          <div>
            <h1 className="text-xl font-semibold">Guías</h1>
            <p className="text-sm text-zinc-400">
              Conocimiento del equipo — cómo se da soporte, cómo se hacen las pruebas, procesos.
            </p>
          </div>
          <AyudaVista titulo="Guías" {...ayudaGuias({ puedeCrear: canEdit(me), admin: isAdmin(me) })} />
        </div>
        <div className="flex flex-wrap items-center gap-2 sm:shrink-0">
          {isAdmin(me) && (
            <button
              onClick={sincronizarQueclink}
              disabled={sincronizando}
              className="btn-ghost text-sm border border-ink-600 disabled:opacity-50"
            >
              {sincronizando ? "Sincronizando…" : "↻ Queclink"}
            </button>
          )}
          {canEdit(me) && (
            <button onClick={() => setEditorOpen(true)} className="btn-primary w-full sm:w-auto shrink-0">
              + Nueva guía
            </button>
          )}
        </div>
      </div>

      <div className="card space-y-3">
        <input
          type="search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Buscar por título, tag o contenido…"
          className="input w-full"
        />

        {tagsDisponibles.length > 0 && (
          <div className="flex flex-wrap items-center gap-1.5">
            <span className="text-xs uppercase tracking-wide text-zinc-500 mr-1">
              Filtrar:
            </span>
            {tagsDisponibles.map((t) => {
              const active = isActivo(t.tag);
              return (
                <button
                  key={t.tag}
                  onClick={() => toggleTag(t.tag)}
                  className={`text-xs px-2 py-0.5 rounded border transition ${
                    active
                      ? "border-accent-500/60 bg-accent-500/20 text-accent-200"
                      : "border-ink-700 bg-ink-800/40 text-zinc-400 hover:text-zinc-200 hover:border-zinc-600"
                  }`}
                >
                  {t.tag}
                  <span
                    className={`ml-1 ${active ? "text-accent-300/80" : "text-zinc-600"}`}
                  >
                    {t.count}
                  </span>
                </button>
              );
            })}
            {activos.length > 0 && (
              <button
                onClick={() => setActivos([])}
                className="text-xs text-zinc-500 hover:text-zinc-300 transition ml-1"
              >
                limpiar filtros
              </button>
            )}
          </div>
        )}
      </div>

      {loading ? (
        <p className="text-sm text-zinc-500">Cargando guías…</p>
      ) : sinResultados ? (
        <div className="card py-8 text-center">
          <p className="text-sm text-zinc-500">
            {q.trim() || activos.length > 0
              ? "Ninguna guía coincide con los filtros."
              : "Aún no hay guías."}
          </p>
        </div>
      ) : (
        <div className="card divide-y divide-ink-700 p-0">
          {items.map((g) => (
            <Link
              key={g.id}
              to={`/guias/${g.id}`}
              className="flex items-center justify-between gap-3 px-4 py-3 hover:bg-ink-800/50 transition"
            >
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 min-w-0">
                  <span className="text-sm font-medium text-zinc-200 truncate">
                    {g.titulo}
                  </span>
                  {g.estado === "borrador" && (
                    <span className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded border border-amber-500/30 bg-amber-500/10 text-amber-300 flex-shrink-0">
                      borrador
                    </span>
                  )}
                </div>
                {g.tags.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-1">
                    {g.tags.map((t) => (
                      <span
                        key={t}
                        className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded border border-ink-700 bg-ink-800/40 text-zinc-400"
                      >
                        {t}
                      </span>
                    ))}
                  </div>
                )}
                <p className="text-xs text-zinc-500 mt-0.5">
                  {g.autor_nombre || "—"} · actualizada{" "}
                  {new Date(g.updated_at).toLocaleDateString()}
                </p>
              </div>
              <span className="text-accent-400 text-sm flex-shrink-0">
                ver →
              </span>
            </Link>
          ))}
        </div>
      )}

      <GuiaEditorModal
        open={editorOpen}
        onClose={() => setEditorOpen(false)}
        onSaved={() => {
          void load();
          void loadTags(); // refrescar chips por si hay tags nuevos
        }}
      />
    </div>
  );
}
